import React, { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { VaultGrowLogo } from '@/components/VaultGrowLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(19,20,19,0.85)',
};

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [showPwd, setShowPwd] = useState({ new_password: false, confirm_password: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Token inválido o faltante.');
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError('La confirmación de contraseña no coincide.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: form.new_password,
      });
      setSuccess('Tu contraseña fue actualizada correctamente. Ya puedes iniciar sesión.');
      setForm({ new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo restablecer la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(158,175,172,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    padding: '12px 42px 12px 38px',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: '460px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <VaultGrowLogo size={40} borderRadius={12} />
            <span style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '28px' }}>VaultGrow</span>
          </div>
          <p style={{ color: C.accentDark, fontSize: '14px' }}>Restablecer contraseña</p>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '28px' }}>
          <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '24px', fontFamily: 'Outfit, sans-serif' }}>Nueva contraseña</h1>
          <p style={{ color: C.accentDark, marginTop: 0, marginBottom: '18px', fontSize: '14px' }}>Ingresa y confirma tu nueva contraseña.</p>

          <form onSubmit={handleSubmit}>
            {[
              ['new_password', 'Nueva contraseña'],
              ['confirm_password', 'Confirmar contraseña'],
            ].map(([field, label]) => (
              <div key={field} style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', color: C.accentDark, fontSize: '12px', marginBottom: '8px' }}>{label}</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={14} color={C.accentDark} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input
                    type={showPwd[field] ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={form[field]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(prev => ({ ...prev, [field]: !prev[field] }))}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.accentDark, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px' }}
                  >
                    {showPwd[field] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            ))}

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}
            {success && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: C.accent, fontSize: '13px', marginBottom: '12px' }}>
                <CheckCircle size={14} /> {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px', borderRadius: '12px', border: 'none',
                background: C.accent, color: '#131413', fontWeight: 800, cursor: loading ? 'default' : 'pointer',
              }}
            >
              {loading ? 'Actualizando...' : 'Guardar nueva contraseña'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <Link to="/login" style={{ color: C.accent, fontSize: '13px', textDecoration: 'none' }}>Volver a iniciar sesión</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
